import { useCallback, useEffect, useRef, useState } from "react";

// Shared look and small helpers for the HR screens. The table / card styles match
// the rest of the app (AdminSetupV2, Expense Management).

export const thStyle = {
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: "13px",
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
};
export const tdStyle = { padding: "12px 16px", fontSize: "14px", color: "#374151", borderBottom: "1px solid #F3F4F6", verticalAlign: "middle" };
export const cardStyle = { background: "#fff", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" };
export const labelStyle = { display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" };
export const inputStyle = { height: "40px", fontSize: "14px", borderRadius: "6px" };
export const iconBtnStyle = { background: "none", border: "none", padding: "4px", cursor: "pointer", color: "#C0335A", display: "inline-flex", alignItems: "center" };
export const pageStyle = { padding: "20px 24px", maxWidth: 1280, margin: "0 auto" };

export const badgeStyle = (bg, color) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

const STATUS_COLOURS = {
  PENDING: ["#FEF3C7", "#92400E"],
  APPROVED: ["#D1FAE5", "#065F46"],
  ISSUED: ["#D1FAE5", "#065F46"],
  VERIFIED: ["#D1FAE5", "#065F46"],
  ACTIVE: ["#D1FAE5", "#065F46"],
  REJECTED: ["#FEE2E2", "#991B1B"],
  CANCELLED: ["#F3F4F6", "#4B5563"],
  WITHDRAWN: ["#F3F4F6", "#4B5563"],
  INACTIVE: ["#F3F4F6", "#4B5563"],
  SENT_BACK: ["#DBEAFE", "#1E40AF"],
  REQUESTED: ["#FEF3C7", "#92400E"],
  SEPARATED: ["#F3F4F6", "#4B5563"],
  NOTICE: ["#FEF3C7", "#92400E"],
  PROBATION: ["#DBEAFE", "#1E40AF"],
};

export const statusLabel = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

export const statusBadge = (status) => {
  const [bg, color] = STATUS_COLOURS[status] || ["#F3F4F6", "#374151"];
  return badgeStyle(bg, color);
};

/* ------------------------------ dates ------------------------------ */

const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayYmd = () => ymd(new Date());

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-10-05" -> "5 Oct 2026". Dates from the API are plain strings; never go through
// Date() for them (a timezone shift would move the day).
export function formatDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!m) return value ? String(value) : "—";
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

// "2026-10-05" to "2026-10-07" -> "5 – 7 Oct 2026"; one day -> "5 Oct 2026".
export function formatRange(from, to) {
  if (!from) return "—";
  if (!to || to === from) return formatDate(from);
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(from);
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(to);
  if (!a || !b) return `${from} – ${to}`;
  if (a[1] === b[1] && a[2] === b[2]) return `${Number(a[3])} – ${Number(b[3])} ${MONTHS[Number(a[2]) - 1]} ${a[1]}`;
  return `${formatDate(from)} – ${formatDate(to)}`;
}

// An instant from the API (ISO string) in the browser's timezone.
export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const formatDays = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${Number.isInteger(v) ? v : v.toFixed(2).replace(/0$/, "")} day${v === 1 ? "" : "s"}`;
};

// Days between two YYYY-MM-DD strings, inclusive of both ends.
export function daysBetweenInclusive(from, to) {
  const a = Date.parse(from + "T00:00:00Z");
  const b = Date.parse(to + "T00:00:00Z");
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

export function addDaysYmd(value, n) {
  const t = Date.parse(value + "T00:00:00Z") + n * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

/* ------------------------------ loading ------------------------------ */

// Runs `fn` on mount and whenever `deps` change; `reload()` runs it again.
// Answers { data, loading, error }. `fn` resolves with the API answer; a
// { success:false } answer becomes the error, otherwise `select(res)` picks the data.
export function useLoad(fn, deps = [], { select = (res) => res?.data, enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: enabled, error: null });
  const alive = useRef(true);
  const seq = useRef(0);

  const run = useCallback(async () => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fn();
      if (!alive.current || mine !== seq.current) return;
      if (res && res.success === false) throw new Error(res.message || res.errors?.[0]?.msg || "Something went wrong");
      setState({ data: select(res), loading: false, error: null });
    } catch (err) {
      if (!alive.current || mine !== seq.current) return;
      setState({ data: null, loading: false, error: err.message || "Something went wrong" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    alive.current = true;
    if (enabled) run();
    return () => {
      alive.current = false;
    };
  }, [run, enabled]);

  return { ...state, reload: run, setData: (data) => setState((s) => ({ ...s, data })) };
}

/* ------------------------------ labels ------------------------------ */

export const REQUEST_LABELS = {
  LEAVE: "Leave",
  LEAVE_CANCEL: "Leave cancellation",
  COMP_OFF: "Comp-off",
  BANK_CHANGE: "Bank details change",
  PROFILE_CHANGE: "Profile change",
  SEPARATION: "Separation",
  LETTER: "Letter request",
  REGULARISATION: "Attendance correction",
  SHIFT_SWAP: "Shift swap",
  OT: "Overtime",
};
export const requestLabel = (module) => REQUEST_LABELS[module] || statusLabel(module);

/* ------------------------------ time ------------------------------ */

// 480 -> "8h", 95 -> "1h 35m", 45 -> "45m", 0 -> "0m", null -> "—".
export function formatMinutes(mins) {
  const n = Number(mins);
  if (mins === null || mins === undefined || mins === "" || !Number.isFinite(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(Math.round(n));
  const h = Math.floor(a / 60);
  const m = a % 60;
  if (!h) return `${sign}${m}m`;
  return `${sign}${h}h${m ? ` ${m}m` : ""}`;
}

// "08:00:00" -> "08:00".
export const hhmm = (t) => (t ? String(t).slice(0, 5) : "—");

// A UTC instant -> "HH:MM" in the browser's timezone. The API sends DATETIME columns as zone-less strings
// ("2026-10-05 04:00:00"), which `new Date()` would read as the browser's own clock, so those are read as UTC.
// Attendance screens show the tenant's clock through `localTime` instead and do not depend on the browser's zone.
export function formatTime(iso) {
  if (!iso) return "—";
  const text = String(iso);
  const d = new Date(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(text) ? `${text.replace(" ", "T")}Z` : iso);
  if (Number.isNaN(d.getTime())) return text;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// The company's wall-clock time from a `*_local` value the API sends ("2026-10-05 08:00:00" -> "08:00"). Plain text, no
// timezone maths: the API already put it on the company's clock (UAE or India time).
export function localTime(stamp) {
  const m = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/.exec(String(stamp || ""));
  return m ? `${m[1]}:${m[2]}` : "—";
}

export const ATTENDANCE_STATUS = {
  PRESENT: { label: "Present", code: "P", bg: "#D1FAE5", color: "#065F46" },
  HALF_DAY: { label: "Half day", code: "H", bg: "#FEF3C7", color: "#92400E" },
  ABSENT: { label: "Absent", code: "A", bg: "#FEE2E2", color: "#991B1B" },
  LOP: { label: "Loss of pay", code: "L", bg: "#FECACA", color: "#7F1D1D" },
  ON_LEAVE: { label: "On leave", code: "LV", bg: "#DBEAFE", color: "#1E40AF" },
  HOLIDAY: { label: "Holiday", code: "HD", bg: "#EDE9FE", color: "#5B21B6" },
  WEEKLY_OFF: { label: "Weekly off", code: "WO", bg: "#F3F4F6", color: "#4B5563" },
  WFH: { label: "Work from home", code: "WF", bg: "#CFFAFE", color: "#155E75" },
  ON_DUTY: { label: "On duty", code: "OD", bg: "#CCFBF1", color: "#115E59" },
  MISSING_PUNCH: { label: "Missing punch", code: "MP", bg: "#FFEDD5", color: "#9A3412" },
};
export const attendanceStatus = (s) => ATTENDANCE_STATUS[s] || { label: statusLabel(s), code: "—", bg: "#F3F4F6", color: "#374151" };
